class ProductCardSwatches extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", () => this._swatchChanged());
  }

  connectedCallback() {
    this._carousel = this.querySelector("[data-swatch-carousel]");
    this._inner = this._carousel?.querySelector("[data-swatch-carousel-inner]");
    this._prevBtn = this.querySelector("[data-swatches-navigation-previous]");
    this._nextBtn = this.querySelector("[data-swatches-navigation-next]");

    if (!this._carousel || !this._inner) return;

    const items = Array.from(this._inner.children);
    if (!items.length) return;

    this._visibleItems = new Set();

    this._itemObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.isIntersecting
            ? this._visibleItems.add(entry.target)
            : this._visibleItems.delete(entry.target);
        });
      },
      { root: this._carousel, threshold: 0 },
    );

    this._endObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.target === items[0]) {
            this._prevBtn.disabled = entry.isIntersecting;
          } else {
            this._nextBtn.disabled = entry.isIntersecting;
          }
        });
      },
      { root: this._carousel, threshold: 0.9 },
    );

    items.forEach((item) => this._itemObserver.observe(item));
    this._endObserver.observe(items[0]);
    this._endObserver.observe(items[items.length - 1]);

    this._prevBtn?.addEventListener("click", () => this._scrollPrev());
    this._nextBtn?.addEventListener("click", () => this._scrollNext());
  }

  disconnectedCallback() {
    this._itemObserver?.disconnect();
    this._endObserver?.disconnect();
  }

  _scrollNext() {
    const items = Array.from(this._inner.children);
    const carouselRect = this._carousel.getBoundingClientRect();
    const visible = items.filter((item) => this._visibleItems.has(item));
    if (!visible.length) return;

    // Scroll so the third-to-last visible swatch becomes the first, keeping 3 visible
    const target =
      visible.length >= 3
        ? visible[visible.length - 3]
        : visible[visible.length - 1];
    const itemRect = target.getBoundingClientRect();
    const scrollLeft =
      itemRect.left - carouselRect.left + this._carousel.scrollLeft;
    this._carousel.scrollTo({ left: scrollLeft, behavior: "smooth" });
  }

  _scrollPrev() {
    const items = Array.from(this._inner.children);
    const carouselRect = this._carousel.getBoundingClientRect();
    const visible = items.filter((item) => this._visibleItems.has(item));
    if (!visible.length) return;

    // Scroll so the third visible swatch becomes the last, keeping 3 visible
    const target = visible.length >= 3 ? visible[2] : visible[0];
    const itemRect = target.getBoundingClientRect();
    const itemAbsoluteLeft =
      itemRect.left - carouselRect.left + this._carousel.scrollLeft;
    const scrollLeft =
      itemAbsoluteLeft + target.offsetWidth - this._carousel.clientWidth;
    this._carousel.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: "smooth",
    });
  }

  _swatchChanged() {
    document.dispatchEvent(new CustomEvent("swatch:changed"));
  }
}

if (!customElements.get("product-card-swatches")) {
  customElements.define("product-card-swatches", ProductCardSwatches);
}
